import React, { useState, useMemo } from 'react';
import { Sheet } from 'tamagui';
import { CobuStack, CobuText, CobuView, CobuAlert } from '@cobu/ui';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Strings } from '../../constants/Strings';

type Props = {
  isCurrentUserCreator: boolean;
  onEditPress: () => void;
  onCancelGatheringPress: () => void;
  onBlockUserPress: () => void;
  onReportPress: () => void;
  title?: string;
  canReport?: boolean;
};

export default function CreatorMenuButton({
  isCurrentUserCreator,
  onEditPress,
  onCancelGatheringPress,
  onBlockUserPress,
  onReportPress,
  title,
  canReport = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const strings = Strings.get().events.menu;
  const common = Strings.get().common;

  const items = useMemo(() => {
    if (isCurrentUserCreator) {
      return [
        { key: 'edit', label: strings.editDetails, onPress: onEditPress },
        { key: 'cancel', label: strings.cancelGathering, onPress: () => setShowCancelConfirm(true), color: '$red' },
      ];
    }
    return [
      ...(canReport
        ? [{ key: 'report', label: strings.reportGatheringAuthor, onPress: onReportPress }]
        : []),
      { key: 'block', label: strings.blockGatheringAuthor, onPress: onBlockUserPress },
    ];
  }, [isCurrentUserCreator, onEditPress, onReportPress, onBlockUserPress, strings, canReport]);

  return (
    <>
      <CobuStack
        role="button"
        tabIndex={0}
        onPress={() => setOpen(true)}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        ai="center"
        jc="center"
        w="$6"
        h="$6"
        br="$4"
        bg="transparent"
        style={{ cursor: 'pointer' }}
      >
        <MoreHorizIcon fontSize="small" />
      </CobuStack>

      {open ? (
        <Sheet
          open
          onOpenChange={(next: boolean) => {
            if (!next) setOpen(false);
          }}
          snapPoints={[getEstimatedHeight(items.length + 1, !!title)]}
          snapPointsMode="constant"
          dismissOnSnapToBottom
          dismissOnOverlayPress
          modal
        >
          <Sheet.Overlay />
          <Sheet.Handle />
          <Sheet.Frame>
            <CobuStack py="$3" gap="$2">
              {title ? (
                <>
                  <CobuText variant="body" fos="$3" fow="500" color="$cobuDarkBlue" ta="center">
                    {title}
                  </CobuText>
                  {renderDivider()}
                </>
              ) : null}
              {items.map((item) => (
                <ActionItem
                  key={item.key}
                  label={item.label}
                  color={item.color as any}
                  onPress={() => {
                    setOpen(false);
                    item.onPress();
                  }}
                />
              ))}

              {renderDivider()}

              <ActionItem label={strings.cancel} onPress={() => setOpen(false)} />
            </CobuStack>
          </Sheet.Frame>
        </Sheet>
      ) : null}

      {/* Cancel gathering confirmation */}
      <CobuAlert
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title={common.alerts.cancelGathering.title}
        description={common.alerts.cancelGathering.message}
        acceptText={common.buttonLabels.yes}
        cancelText={common.buttonLabels.no}
        onAccept={() => {
          setShowCancelConfirm(false);
          onCancelGatheringPress();
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </>
  );
}

function getEstimatedHeight(itemCount: number, includeTitle: boolean) {
  const itemHeight = 48;
  const dividerHeight = 8;
  const basePadding = 0;
  const titleHeight = includeTitle ? 56 : 0;
  const titleDiv = includeTitle ? dividerHeight : 0;
  return itemHeight * itemCount + dividerHeight * 2 + basePadding + titleHeight + titleDiv;
}

function ActionItem({ label, onPress, color }: { label: string; onPress: () => void; color?: string }) {
  return (
    <CobuView onPress={onPress}>
      <CobuStack
        layout="horizontal"
        ai="center"
        jc="center"
        gap="$3"
        p="$3"
        br="$2"
        hoverStyle={{ background: '$backgroundMuted' }}
      >
        <CobuText variant="body" fos="$3" fow="500" color={(color as any)}>
          {label}
        </CobuText>
      </CobuStack>
    </CobuView>
  );
}

function renderDivider() {
  return <CobuStack h={1} bg="$borderColor" mx="$2" />;
}
